import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Switch, useToast } from '@qh/shared';
import { ApiError, candidateApi, type CandidatePreferences } from '../../lib/apiClient';

/**
 * My Preferences (analysis §5.7) — three aspirational booleans the candidate can
 * tune today; the platform features that will read them ship in later phases.
 *
 * UX: each toggle auto-saves on change with a 300ms debounce so quick double-taps
 * collapse to a single PATCH. We optimistically apply the change locally, fire the
 * PATCH, and revert on failure with a toast. A small spinner next to the toggle
 * shows the in-flight save.
 */

type PrefKey = keyof CandidatePreferences;

const TOGGLES: { key: PrefKey; label: string; description: string }[] = [
  {
    key: 'allow_recruiter_messages',
    label: 'Allow recruiters to message you directly',
    description:
      'Recruiters at companies on QuantumHire will be able to DM you about job opportunities. (Coming soon — your preference is saved.)',
  },
  {
    key: 'share_profile_with_network',
    label: 'Share your profile with companies in the QuantumHire Network',
    description:
      'Companies sourcing candidates may see your profile when they search by skills or experience. (Coming soon — your preference is saved.)',
  },
  {
    key: 'email_weekly_job_recommendations',
    label: 'Email me weekly job recommendations',
    description:
      'A weekly digest of jobs that match your profile. (Coming soon — your preference is saved.)',
  },
];

export function MyPreferencesPage() {
  const toast = useToast();
  const [prefs, setPrefs] = useState<CandidatePreferences | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingKey, setPendingKey] = useState<PrefKey | null>(null);
  const fetchedOnceRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (fetchedOnceRef.current) return;
    let cancelled = false;
    candidateApi.preferences
      .get()
      .then((p) => {
        if (cancelled) return;
        fetchedOnceRef.current = true;
        setPrefs(p);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof ApiError ? err.message : 'Could not load your preferences.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleToggle = useCallback(
    (key: PrefKey, next: boolean) => {
      if (!prefs) return;
      const previous = prefs[key];
      // Optimistic local update.
      setPrefs({ ...prefs, [key]: next });
      // Debounce the PATCH so rapid double-taps coalesce to a single network call.
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        setPendingKey(key);
        try {
          const updated = await candidateApi.preferences.update({ [key]: next });
          setPrefs(updated);
        } catch (err) {
          // Revert on failure.
          setPrefs((prev) => (prev ? { ...prev, [key]: previous } : prev));
          toast.error(
            err instanceof ApiError ? err.message : 'Could not save your preference.',
          );
        } finally {
          setPendingKey(null);
        }
      }, 300);
    },
    [prefs, toast],
  );

  return (
    <section>
      <header className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-brand-primary">
          Preferences
        </h1>
        <p className="text-sm text-brand-muted mt-1">
          Control how recruiters and our matching engine reach out to you.
        </p>
      </header>

      {loadError && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-2xl p-4 mb-4 text-sm">
          {loadError}
        </div>
      )}

      <div className="bg-white border border-gray-100 rounded-3xl shadow-sm p-5 sm:p-6 space-y-5">
        {prefs === null ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
          </div>
        ) : (
          TOGGLES.map(({ key, label, description }, i) => (
            <div
              key={key}
              className={
                i > 0 ? 'pt-5 border-t border-gray-100 flex items-start gap-3' : 'flex items-start gap-3'
              }
            >
              <div className="flex-1 min-w-0">
                <Switch
                  checked={prefs[key]}
                  onChange={(next) => handleToggle(key, next)}
                  label={label}
                  description={description}
                />
              </div>
              {pendingKey === key && (
                <Loader2 className="w-4 h-4 animate-spin text-brand-muted mt-1 shrink-0" />
              )}
            </div>
          ))
        )}
      </div>
    </section>
  );
}
